// Banded model answers: the student reads what each level actually looks
// like, with annotated evidence. Weakest band first so the progression reads
// upward; annotations quote the response's own text.
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { api } from '@/lib/api';
import AppShell from '@/components/AppShell';
import { cn } from '@/lib/utils';

const BAND_LABEL = {
  clb5: 'Around CLB 5', clb7: 'Around CLB 7', clb9: 'Around CLB 9',
  low: 'Low band', mid: 'Mid band', high: 'High band',
};

export default function ExemplarViewer() {
  const { exam, taskType } = useParams();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [active, setActive] = useState(0);

  useEffect(() => {
    api.exemplars(exam, taskType)
      .then((res) => setRows(res.data.exemplars))
      .catch((err) => setError(err.response?.data?.detail || 'No model answers for this task yet.'));
  }, [exam, taskType]);

  const bands = useMemo(() => {
    if (!rows) return [];
    const order = [];
    rows.forEach((r) => { if (!order.includes(r.band)) order.push(r.band); });
    return order;
  }, [rows]);

  if (error) {
    return (
      <AppShell>
        <p className="text-slate-500">{error}</p>
        <Link to={`/${exam}/practice`} className="cta mt-6">Back to practice</Link>
      </AppShell>
    );
  }
  if (!rows) return <AppShell><p className="text-slate-400">Loading…</p></AppShell>;

  const current = rows[active];

  return (
    <AppShell>
      <Link to={`/${exam}/practice`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" /> Practice
      </Link>
      <p className="mono-stat mt-6">{exam} · model answers</p>
      <h1 className="h-display mt-2 text-3xl">What each level looks like.</h1>
      <p className="mt-2 max-w-xl text-[15px] leading-relaxed text-slate-500">
        Real responses at each band, annotated line by line. Read upward: notice what the
        next band does that yours does not do yet.
      </p>

      <div className="mt-8 flex flex-wrap gap-2">
        {rows.map((r, i) => (
          <button
            key={r.exemplar_id}
            type="button"
            onClick={() => setActive(i)}
            data-testid={`exemplar-tab-${i}`}
            className={cn(
              'border px-4 py-2 text-sm font-medium',
              i === active ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 bg-white text-slate-600 hover:border-slate-500'
            )}
          >
            {BAND_LABEL[r.band] || r.band}{rows.filter((x) => x.band === r.band).length > 1 ? ` · ${rows.filter((x, xi) => x.band === r.band && xi <= i).length}` : ''}
          </button>
        ))}
      </div>

      {current && (
        <div className="mt-6 grid gap-6 lg:grid-cols-5">
          <div className="border border-slate-200 bg-white lg:col-span-3">
            <div className="flex items-baseline justify-between border-b border-slate-200 px-6 py-3">
              <p className="mono-stat">{BAND_LABEL[current.band] || current.band}</p>
              <p className="mono-stat">{current.word_count} words{current.kind === 'spoken_transcript' ? ' · spoken transcript' : ''}</p>
            </div>
            <p className="whitespace-pre-line px-6 py-5 text-[15px] leading-8 text-slate-800" data-testid="exemplar-text">
              {current.response_text}
            </p>
          </div>
          <div className="space-y-4 lg:col-span-2">
            <div className="border border-slate-200 bg-white px-5 py-4">
              <p className="mono-stat">why this band</p>
              <p className="mt-2 text-sm leading-7 text-slate-700">{current.why_this_band}</p>
            </div>
            {(current.annotations || []).map((a, i) => (
              <div key={i} className="border-l-2 border-indigo-600 bg-white px-4 py-3">
                <p className="text-sm font-medium text-slate-900">&ldquo;{a.quote}&rdquo;</p>
                <p className="mt-1 text-sm leading-6 text-slate-600">{a.note}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </AppShell>
  );
}
